import { useEffect, useRef } from 'react';
import type { OwnerSurfaceAlertInput, OwnerSurfaceAlertKind } from '@term-deck/core/owner-surface-alerts.js';
import { ownerSurfaceAudioReference, type OwnerSurfaceAudioReference } from '@term-deck/core/owner-surface-alerts.js';
import { jsonFetch } from '../api/http';
import { nativeApp } from './native-app';

/**
 * #443 (FULL do dono, fala 18c71de1: "esse alerta tem que chegar na orquestração") — todo alerta técnico que a conversa
 * do dono exibe vira evento para o motor. O evento é gravado na fila do aparelho no mesmo instante em que o aviso aparece
 * e sobe para `POST /api/owner-surface-alerts` até o servidor confirmar o id; sem rede, chega quando a conexão voltar.
 * `shownSeq` conta as exibições desta fila: se a fila se perder ou estourar, o buraco na sequência mostra ao motor quantos
 * avisos ele não recebeu. Contrato: `docs/owner-surface-alerts.md`.
 *
 * cd0ee1db (23/10/2026) — cada página tem a própria fila (`stream`) numa chave só dela, e só ela escreve ali. Antes, as
 * páginas do mesmo aparelho dividiam uma chave: no stream 4541f315 o shownSeq voltou de 24 para 23, uma página gravou
 * por cima da outra uma cópia sem o delivery_attention 86191209, e a retirada dele nunca saiu. Uma página só mexe na
 * chave de outra quando ela está sem sinal há mais de 3 min: leva a fila dela e retira os avisos abertos como `page_closed`.
 */
/** Estado compartilhado da versão anterior: adotado como página morta quando nenhuma aba antiga escreve mais nele. */
const LEGACY_KEY = 'td-owner-surface-alerts';
const PAGE_PREFIX = 'td-owner-surface-alerts:page:';
const MAX_QUEUE = 500;
/** Página sem sinal há mais que isso fechou com avisos abertos: eles são retirados como `page_closed`. */
const PAGE_STALE_MS = 3 * 60_000;
const HEARTBEAT_MS = 60_000;

interface OpenEpisode { episodeId: string; kind: OwnerSurfaceAlertKind; audio?: OwnerSurfaceAudioReference }
interface PageState { stream: string; seq: number; queue: OwnerSurfaceAlertInput[]; open: Record<string, OpenEpisode>; beatAt: number }
interface LegacyState { stream: string; queue?: OwnerSurfaceAlertInput[]; open?: Record<string, OpenEpisode>; pages?: Record<string, number> }

const randomId = (): string => (globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`).replace(/[^A-Za-z0-9_-]/g, '');
let pageId = randomId();
/** A cópia em memória é a verdade desta página (escritora única); o armazenamento serve à página que vier depois. */
let memory: PageState | null = null;
const pageKey = () => `${PAGE_PREFIX}${pageId}`;

function load(): PageState {
  return (memory ??= { stream: randomId().slice(0, 32), seq: 0, queue: [], open: {}, beatAt: Date.now() });
}

function save(s: PageState): void {
  memory = s;
  s.beatAt = Date.now();
  try { localStorage.setItem(pageKey(), JSON.stringify(s)); } catch { /* cota ou aba privada: a cópia em memória continua */ }
}

/** `undefined` = chave ausente (ou leitura atrasada de outra página); `null` = conteúdo ilegível. */
function readJson<T>(key: string): T | null | undefined {
  let raw: string | null;
  try { raw = localStorage.getItem(key); } catch { return undefined; }
  if (raw === null) return undefined;
  try { return JSON.parse(raw) as T; } catch { return null; }
}

function deviceId(): string {
  try {
    const known = localStorage.getItem('td-owner-device');
    if (known) return known;
    const id = crypto.randomUUID(); localStorage.setItem('td-owner-device', id); return id;
  } catch { return 'unknown-device'; }
}

function push(s: PageState, event: OwnerSurfaceAlertInput): void {
  s.queue.push(event);
  // Fila estourada perde as mais antigas; o buraco de sequência mostra ao motor o que se perdeu.
  if (s.queue.length > MAX_QUEUE) s.queue.splice(0, s.queue.length - MAX_QUEUE);
}

function base(kind: OwnerSurfaceAlertKind, episodeId: string) {
  return {
    id: randomId(), episodeId, kind, surface: nativeApp() ? 'android' as const : 'web' as const, conversation: 'donizeti' as const,
    deviceId: deviceId(), atIso: new Date().toISOString(), ...(typeof location === 'undefined' ? {} : { path: location.pathname }),
  };
}

/** Leva a fila da página morta (o servidor ignora id repetido) e retira os avisos que ela deixou abertos. */
function adopt(s: PageState, queue: OwnerSurfaceAlertInput[] | undefined, open: OpenEpisode[], stream: string): void {
  for (const e of queue ?? []) if (!s.queue.some(q => q.id === e.id)) push(s, e);
  for (const ep of open) {
    const audio = ownerSurfaceAudioReference(ep.audio);
    push(s, { ...base(ep.kind, ep.episodeId), phase: 'cleared', stream, clearedBy: 'page_closed', ...(audio ? { audio } : {}) });
  }
}

/** Páginas que fecharam com avisos abertos ou fila por enviar; a página viva renova o próprio sinal ao gravar. */
function adoptDeadPages(s: PageState, now: number): void {
  const keys: string[] = [];
  try { for (let i = 0; i < localStorage.length; i++) { const k = localStorage.key(i); if (k) keys.push(k); } } catch { return; }
  for (const key of keys) {
    if (key === pageKey()) continue;
    if (key.startsWith(PAGE_PREFIX)) {
      const other = readJson<PageState>(key);
      if (other === undefined || (other && now - other.beatAt <= PAGE_STALE_MS)) continue;
      if (other) adopt(s, other.queue, Object.values(other.open ?? {}), other.stream);
    } else if (key === LEGACY_KEY) {
      const old = readJson<LegacyState>(key);
      // Aba da versão anterior ainda viva escreve nessa chave: fica com ela até fechar.
      if (old === undefined || (old && Object.values(old.pages ?? {}).some(seen => now - seen <= PAGE_STALE_MS))) continue;
      if (old) adopt(s, old.queue, Object.values(old.open ?? {}), old.stream);
    } else continue;
    try { localStorage.removeItem(key); } catch { /* sem armazenamento */ }
  }
}

let started = false;
let beat: ReturnType<typeof setInterval> | null = null;
function start(): void {
  if (started || typeof window === 'undefined') return;
  started = true;
  const s = load(); adoptDeadPages(s, Date.now()); save(s);
  const wake = () => { retryMs = 5_000; void flushOwnerSurfaceAlerts(); };
  window.addEventListener('online', wake);
  window.addEventListener('focus', wake);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') wake(); });
  beat = setInterval(() => { const st = load(); adoptDeadPages(st, Date.now()); save(st); if (st.queue.length) void flushOwnerSurfaceAlerts(); }, HEARTBEAT_MS);
  (beat as { unref?: () => void }).unref?.();
  if (s.queue.length) void flushOwnerSurfaceAlerts();
}

/** O aviso `key` apareceu na tela. Repetir com o mesmo tipo enquanto ele está aberto não gera outro evento. */
export function ownerAlertShown(key: string, kind: OwnerSurfaceAlertKind, text?: string, reference?: OwnerSurfaceAudioReference): void {
  start();
  const s = load();
  const current = s.open[key];
  if (current?.kind === kind) return;
  if (current) push(s, { ...base(current.kind, current.episodeId), phase: 'cleared', stream: s.stream, clearedBy: 'gone', ...(current.audio ? { audio: current.audio } : {}) });
  const episodeId = randomId();
  const audio = kind.startsWith('audio_') ? ownerSurfaceAudioReference(reference) : undefined;
  s.seq += 1;
  push(s, { ...base(kind, episodeId), phase: 'shown', stream: s.stream, shownSeq: s.seq, ...(text ? { text: text.slice(0, 1000) } : {}), ...(audio ? { audio } : {}) });
  s.open[key] = { episodeId, kind, ...(audio ? { audio } : {}) };
  save(s);
  void flushOwnerSurfaceAlerts();
}

/** O aviso `key` saiu da tela. */
export function ownerAlertCleared(key: string): void {
  const s = load();
  const current = s.open[key];
  if (!current) return;
  push(s, { ...base(current.kind, current.episodeId), phase: 'cleared', stream: s.stream, clearedBy: 'gone', ...(current.audio ? { audio: current.audio } : {}) });
  delete s.open[key];
  save(s);
  void flushOwnerSurfaceAlerts();
}

let inFlight: Promise<void> | null = null;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let retryMs = 5_000;

/** Envia a fila em lotes; só apaga o que o servidor confirmou. Falha agenda nova tentativa com espera crescente. */
export function flushOwnerSurfaceAlerts(): Promise<void> {
  if (inFlight) return inFlight;
  inFlight = (async () => {
    // Fila vazia terminaria síncrona e o `finally` rodaria antes desta atribuição, deixando `inFlight` preso para sempre.
    await null;
    try {
      for (;;) {
        const batch = load().queue.slice(0, 100);
        if (!batch.length) break;
        const r = await jsonFetch<{ ids: string[] }>('/api/owner-surface-alerts', { method: 'POST', body: JSON.stringify({ events: batch }), headers: { 'content-type': 'application/json' } });
        const acked = new Set(r.ids);
        const s = load(); s.queue = s.queue.filter(e => !acked.has(e.id)); save(s);
        if (!batch.some(e => acked.has(e.id))) throw new Error('owner_surface_alerts_not_acked');
      }
      retryMs = 5_000;
    } catch {
      if (retryTimer) clearTimeout(retryTimer);
      retryTimer = setTimeout(() => { retryTimer = null; void flushOwnerSurfaceAlerts(); }, retryMs);
      (retryTimer as { unref?: () => void }).unref?.();
      retryMs = Math.min(retryMs * 2, 5 * 60_000);
    } finally { inFlight = null; }
  })();
  return inFlight;
}

/** Liga um aviso da tela à telemetria: `kind` presente = aviso visível; `null` (ou desmontar) = aviso retirado. */
export function useOwnerSurfaceAlert(key: string, kind: OwnerSurfaceAlertKind | null, text?: string, audio?: OwnerSurfaceAudioReference): void {
  const reported = useRef<OwnerSurfaceAlertKind | null>(null);
  // 9efe3c8f: a telemetria liga quando a conversa monta, com ou sem aviso. Ligando só no primeiro aviso, a página nova sem
  // aviso nunca retirava os que a página morta deixou abertos (Xiaomi d8834256: 13 h de "Offline" aberto no motor).
  useEffect(() => { start(); }, []);
  // Roda a cada commit e compara com o último tipo reportado. Com `[key, kind]` o React 19 deixou de disparar a troca
  // `null → offline_header` depois de uma renderização interrompida: o "Offline" ficou na tela e o motor não soube (E2E #443).
  // O texto não abre episódio novo: o mesmo aviso com outra redação continua sendo o mesmo episódio.
  useEffect(() => {
    if (kind === reported.current) return;
    if (kind) ownerAlertShown(key, kind, text, audio); else ownerAlertCleared(key);
    reported.current = kind;
  });
  useEffect(() => () => {
    if (reported.current) ownerAlertCleared(key);
    reported.current = null;
  }, [key]);
}

/** Só para testes: a chave do armazenamento desta página. */
export function ownerSurfaceAlertsPageKeyForTest(): string {
  return pageKey();
}

/** Só para testes: zera o estado do módulo e simula uma página nova. */
export function resetOwnerSurfaceAlertsForTest(): void {
  memory = null; started = false; inFlight = null; retryMs = 5_000; pageId = randomId();
  if (beat) { clearInterval(beat); beat = null; }
  if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
  try { for (const k of Object.keys(localStorage)) if (k === LEGACY_KEY || k.startsWith(PAGE_PREFIX)) localStorage.removeItem(k); } catch { /* sem armazenamento */ }
}
